import { useCallback, useEffect, useState } from 'react';
import BaseButton from 'components/ui/BaseButton';
import Modal, {
  ModalContent,
  ModalFooter,
  ModalHeader
} from 'components/ui/Modal';
import CheckBox from 'components/common/inputs/CheckBox';
import { POLICY_CONSENT_LABEL_KEYS } from 'constants/policy-consent';
import { ConsentStatus } from 'interfaces/consent-status.interface';
import {
  acceptPolicies,
  getConsentStatus
} from 'services/consent-status.service';
import { t } from 'services/i18n.service';
import { captureException } from 'services/posthog.service';
import { getOutdatedPolicies } from 'utils/policy-consent.util';

interface PolicyConsentGateProps {
  userId?: string;
}

const CONSENT_TITLE_ID = 'policy-consent-title';
const AGREE_CHECKBOX_ID = 'policy-consent-agree';

// The gate can't be dismissed; the only way out is agreeing.
const keepOpen = () => undefined;

/**
 * Blocks the dashboard until the signed-in user has accepted the current privacy policy and terms (ST4S PR1C#,
 * PR16#). Covers every sign-up path, including social and guest sign-ups that never showed a checkbox, and asks
 * again whenever the server's policy version moves on.
 */
const PolicyConsentGate = ({ userId }: PolicyConsentGateProps) => {
  const [status, setStatus] = useState<ConsentStatus | null>(null);
  const [isAgreed, setIsAgreed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaveFailed, setHasSaveFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await getConsentStatus());
    } catch (error) {
      // An older API without consent-status, or a network failure, must not lock everyone out of the dashboard.
      setStatus(null);
      captureException(error);
    }
  }, []);

  useEffect(() => {
    if (userId) void load();
  }, [userId, load]);

  if (!status?.requires_consent) return null;

  const accept = async () => {
    setIsSaving(true);
    setHasSaveFailed(false);
    try {
      await acceptPolicies(status);
      await load();
    } catch (error) {
      setHasSaveFailed(true);
      captureException(error);
    } finally {
      setIsSaving(false);
    }
  };

  const isFirstConsent = getOutdatedPolicies(status).every(
    ([, policy]) => policy.accepted_version === null
  );

  return (
    <Modal
      isOpen
      onClose={keepOpen}
      closeOnOverlayClick={false}
      closeOnEscape={false}
      size='small'
      zIndex='z-[999999]'
      ariaLabelledById={CONSENT_TITLE_ID}>
      <div className='contents' data-testid='policy-consent-gate'>
        <ModalHeader
          titleId={CONSENT_TITLE_ID}
          title={t(
            isFirstConsent
              ? 'policy_consent.title_first'
              : 'policy_consent.title_updated'
          )}
        />
        <ModalContent className='flex flex-col ~gap-3/4 ~px-4/6'>
          <p className='~text-xs/base'>
            {t(
              isFirstConsent
                ? 'policy_consent.description_first'
                : 'policy_consent.description_updated'
            )}
          </p>
          <ul className='list-disc pl-5 ~text-xs/base'>
            {getOutdatedPolicies(status).map(([type, policy]) => (
              <li key={type}>
                <a
                  className='text-blue-500 hover:text-blue-700 underline'
                  href={policy.url}
                  target='_blank'
                  rel='noreferrer'>
                  {t(POLICY_CONSENT_LABEL_KEYS[type])}
                </a>{' '}
                {t('policy_consent.version', {
                  version: policy.current_version
                })}
              </li>
            ))}
          </ul>
          <CheckBox
            id={AGREE_CHECKBOX_ID}
            text={t('policy_consent.agree')}
            checked={isAgreed}
            onChange={({ target: { checked } }) => setIsAgreed(checked)}
            dims='min-w-4 ~w-4/5 min-h-4 ~h-4/5'
          />
          {hasSaveFailed && (
            <p role='alert' className='text-semantic-error ~text-xs/sm'>
              {t('policy_consent.save_failed')}
            </p>
          )}
        </ModalContent>
        <ModalFooter>
          <BaseButton
            disabled={!isAgreed || isSaving}
            isLoading={isSaving}
            onClick={() => void accept()}
            title={t('policy_consent.continue')}
          />
        </ModalFooter>
      </div>
    </Modal>
  );
};

export default PolicyConsentGate;
